import React, { useState, useEffect } from 'react';
import { Card, Typography, Empty, Spin } from 'antd';
import {
  BookOutlined,
  PlusCircleOutlined,
  SettingOutlined,
  AlertOutlined,
  EyeOutlined,
  DesktopOutlined,
} from '@ant-design/icons';
import { parseMarkdown } from './MarkdownParser';
import { styles, contentStyles } from './styles';

const { Title } = Typography;

const helpSections = [
  {
    id: 'create-tags',
    title: 'Create a Tag',
    icon: <DesktopOutlined />,
    filename: 'help-center%2Fcreate-tags.md',
  },
  {
    id: 'install-tags',
    title: 'Tag Installation via Google Tag Manager',
    icon: <PlusCircleOutlined />,
    filename: 'help-center%2Finstall-tags.md',
  },
  {
    id: 'test-tags',
    title: 'Tag Testing',
    icon: <SettingOutlined />,
    filename: 'help-center%2Ftest-tags.md',
  },
  {
    id: 'conversion-tags',
    title: 'Paid Search Tag Conversion Setup',
    icon: <AlertOutlined />,
    filename: 'help-center%2Fconversion-tags.md',
  },
  {
    id: 'check-tags',
    title: 'Checking Paid Search Tag Implementation',
    icon: <EyeOutlined />,
    filename: 'help-center%2Fcheck-tags.md',
  },
];

export default function TagsHelpCenterPage() {
  const [loading, setLoading] = useState(true);
  const [markdownContent, setMarkdownContent] = useState('');
  const [selectedId, setSelectedId] = useState('create-tags');

  useEffect(() => {
    const activeSection = helpSections.find((section) => section.id === selectedId);
    if (!activeSection || !activeSection.filename) return;

    setLoading(true);
    fetch(
      `https://internal-service.dmp.mediajel.ninja/api/docs/html?filename=${activeSection.filename}`
    )
      .then((res) => res.text())
      .then((content) => {
        setMarkdownContent(content);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error loading markdown:', err);
        setLoading(false);
      });
  }, [selectedId]);

  useEffect(() => {
    const styleId = 'tag-help-content-styles';
    let styleElement = document.getElementById(styleId);

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    styleElement.textContent = contentStyles;

    return () => {
      if (styleElement && styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
    };
  }, []);

  const parsedContent = markdownContent ? parseMarkdown(markdownContent) : '';
  const activeSection = helpSections.find((section) => section.id === selectedId);

  const renderContent = () => {
    return parsedContent ? (
      <div
        className="tag-help-content"
        style={styles.renderedContent}
        dangerouslySetInnerHTML={{ __html: parsedContent }}
      />
    ) : (
      <Empty description="No documentation available" />
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.sidebar}>
        <Title level={5}>List of Documentation</Title>
        <ul style={styles.topicsList}>
          {helpSections.map((section) => (
            <li
              key={section.id}
              onClick={(e) => {
                // Clear any inline styles that might interfere
                e.target.style.backgroundColor = "";
                e.target.style.color = "";
                e.target.style.border = "";
                setSelectedId(section.id);
              }}
              style={{
                ...styles.topicItem,
                ...(selectedId === section.id ? styles.topicItemSelected : styles.topicItemDefault),
              }}
              onMouseEnter={(e) => {
                if (selectedId !== section.id) {
                  e.target.style.backgroundColor =
                    styles.topicItemHover.backgroundColor;
                  e.target.style.color = "#666";
                  e.target.style.border = "1px solid #e8e8e8";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedId !== section.id) {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.color = "#666";
                  e.target.style.border = "1px solid transparent";
                }
              }}
            >
              {section.icon}
              <span>{section.title}</span>
            </li>
          ))}
        </ul>
      </div>
      <div style={styles.content}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>Loading help documentation...</div>
          </div>
        ) : (
          <Card
            title={
              <span>
                {activeSection && activeSection.icon} {activeSection && activeSection.title}
              </span>
            }
            bordered
          >
            {renderContent()}
          </Card>
        )}
      </div>
    </div>
  );
}
